import { api, ApiError } from './api';
import type { Me, CompanyMembership, CompanySwitchResult } from '../api/client';
import { loadRbacContext, clearRbacContext } from './capabilities';
import { purgeSessionCaches } from './swCache';
import { getDeviceId } from '../utils/device';
import { authDeviceClass, authViewportHint } from '../utils/viewport';
import { t } from '../i18n';

// Action name the magic-link token is minted under. The server rejects a token
// carrying any other action, so this must match magicLinkAction in
// pkg/api/router.go.
const MAGIC_LINK_ACTION = 'magic_link';

// In-memory access-token storage. Per the auth-package contract, the
// short-lived JWT rides in the Authorization: Bearer header on every
// protected request; the long-lived refresh_token is in an HttpOnly
// cookie scoped to /api/auth and JS can't read it. On reload, we
// silently exchange the cookie for a fresh access token via POST
// /api/auth/refresh.
let accessToken: string | null = null;
let cachedMe: Me | null = null;

// "This browser was explicitly signed out" flag — the boot-time gate for the
// establishSession() probes (issue 444). Every anonymous page view otherwise
// fires GET /api/me then POST /api/auth/refresh, both 401ing: two failing,
// logged network calls per view on a browser that has never signed in.
//
// REDESIGNED per Valentin's PR 471 review: the original design tracked a
// "signedIn" hint that had to be PRESENT to justify attempting the probes.
// That inverted the safe failure direction — three independent ways land on
// "valid 30-day refresh cookie, hint absent, treated as signed out":
//   - deploy day: no existing browser has the key yet;
//   - Safari ITP evicts script-writable storage after 7 idle days while the
//     server-set HttpOnly refresh cookie survives;
//   - a store where setItem throws (quota-full) but getItem returns null
//     fails CONFIDENTLY false, not open — sign-in succeeds, every reload
//     after it loops back to signed-out.
// Tracking the OPPOSITE signal — an EXPLICIT, definitive "no session"
// declaration — fixes this structurally: ABSENCE of the flag (never set,
// evicted, failed to persist) now means "attempt the bootstrap", which is
// the safe direction to fail in. The one trade-off this reintroduces: a
// browser that has NEVER signed in also has no flag, so it attempts (and
// 401s) on every visit — deliberately accepted as the lesser cost next to
// silently stranding a live session.
//
// Set only on an EXPLICIT, definitive "no session" signal: a user-initiated
// logout (below), or a definitive 401/404 from POST /api/auth/refresh (the
// only refresh outcome that actually proves this browser has no session —
// establishSession's catch below). Any other refresh failure (5xx during a
// revision swap, a Cloud Armor 429/403, a network drop) must leave the flag
// untouched so the next boot gets a normal retry instead of being gated off
// forever.
//
// Cleared on any successful auth establishment (setAccessToken below), so a
// real sign-in — including one that follows an earlier explicit logout on
// the same browser — is never treated as signed-out on its next reload.
//
// Naming precedent: app.device.id (utils/device.ts), app.reauth.roleChanged
// (below).
const EXPLICITLY_SIGNED_OUT_KEY = 'app.session.signedOut';

// isExplicitlySignedOut reports whether boot should SKIP the
// session-establishing probes. Storage access is best-effort; a throw
// (private mode / disabled storage) is treated the same as "flag absent" —
// false — so a broken store degrades to ATTEMPTING the bootstrap, the safe
// direction, never to silently skipping it.
export function isExplicitlySignedOut(): boolean {
  try {
    return localStorage.getItem(EXPLICITLY_SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function markExplicitlySignedOut(): void {
  try {
    localStorage.setItem(EXPLICITLY_SIGNED_OUT_KEY, '1');
  } catch {
    // Best-effort — a failed write just means the NEXT boot attempts the
    // bootstrap instead of skipping it, which is the safe direction to fail
    // in (see the design note above).
  }
}

function clearExplicitlySignedOut(): void {
  try {
    localStorage.removeItem(EXPLICITLY_SIGNED_OUT_KEY);
  } catch {
    // Nothing to do — an unreadable store cannot hold a stale flag either.
  }
}

// Set when a silent refresh is rejected specifically because the device class
// changed since sign-in (401 DEVICE_CLASS_CHANGED) — the server forces re-auth
// without rotating the token. This is distinct from an ordinary expired/invalid
// refresh (REFRESH_TOKEN_INVALID / _REPLAY): those are plain logouts, this is a
// security sign-out the user should be told about instead of being bounced to
// /login silently (FE-013). The boot flow consumes it once to route to the
// login page's "signed out for security" state.
let forcedReauth = false;

// consumeForcedReauth reports whether the last session-establishment attempt was
// rejected by the device-class-change security check, and clears the flag so it
// is surfaced exactly once. The generic result carries no session detail — the
// caller only learns that a security sign-out happened, never why (FE-013.2).
export function consumeForcedReauth(): boolean {
  const was = forcedReauth;
  forcedReauth = false;
  return was;
}

// errorCodeOf reads the machine `code` off an ApiError body ({ error, code }),
// or '' when absent — the same shape the auth endpoints return.
function errorCodeOf(err: unknown): string {
  if (!(err instanceof ApiError)) return '';
  const body: unknown = err.body;
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

interface VerifyOrRefreshResponse {
  accessToken: string;
  user?: { id: string; email: string; plan?: string; isNew?: boolean };
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  // A real token install is a successful auth establishment (issue 444
  // redesign): clear any stale signed-out flag so a later reload attempts
  // the bootstrap normally, rather than treating a fresh sign-in — possibly
  // one that follows an earlier explicit logout on this same browser — as
  // still signed out. A null token does NOT mark the flag here: only the two
  // definitive "no session" signals do (logout, a definitive 401/404 from
  // refresh — see establishSession below), so an ordinary transient clear
  // leaves the flag exactly as it was.
  if (token) {
    clearExplicitlySignedOut();
  }
}

// Session id of the device we're signed in on, read from the access token's
// `sid` claim. The session-list endpoint can't identify the current session
// server-side — the refresh_token cookie that would mark it is path-scoped to
// /api/auth and isn't sent to /api/users/me/sessions — so the SPA matches
// `sid` against each session's id instead. Returns null when there's no token
// or it can't be decoded (the JWT is already trusted; we only read a claim).
export function currentSessionId(): string | null {
  const sid = accessTokenClaims()?.sid;
  return typeof sid === 'string' ? sid : null;
}

// accessTokenClaims decodes the in-memory access token's JWT payload. The token
// is already server-verified on every protected call — we only read a claim for
// display, never for an authorization decision. Returns null when there is no
// token or the payload can't be base64/JSON-decoded.
function accessTokenClaims(): Record<string, unknown> | null {
  if (!accessToken) return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// currentUserEmail reads the signed-in user's OWN email from the access token's
// `email` claim (vfco-auth-package mints it into every token; company.go re-mints
// it on switch). Same posture as currentSessionId — the JWT is already trusted,
// we only read a claim to show identity in the account menu (FE-010). It is the
// caller's own token, so this exposes no PII the client didn't already hold.
// Null when there's no token, it can't be decoded, or it carries no email.
export function currentUserEmail(): string | null {
  const email = accessTokenClaims()?.email;
  return typeof email === 'string' && email !== '' ? email : null;
}

export async function authInit(): Promise<void> {
  await establishSession();
  // With a session in hand, make the token carry the ACTIVE company's role
  // (AUTH-001): the auth-package mints tokens with the user's global role, so a
  // bound session re-binds to re-mint after a refresh (AUTH-001.5) and a
  // single-company session auto-binds (AUTH-001.3). A still-unbound multi-company
  // session is left for the boot flow's picker (ensureCompanyChosen).
  if (cachedMe) {
    await reconcileActiveCompany();
    // Seed the RBAC mirror once the company is settled, so nav and screens
    // render from the context payload rather than /api/me's module-only map
    // (). An unbound multi-company session gets a zero-grant payload —
    // correct: the picker runs before anything capability-gated renders.
    await loadRbacContext();
  }
}

// establishSession leaves cachedMe + accessToken populated for an authenticated
// caller (or both null): /api/me directly when a token is already in memory,
// otherwise the silent refresh first and /api/me with the token it mints.
async function establishSession(): Promise<void> {
  // 1. Probe /api/me only when we already hold an in-memory access token (a
  //    verify earlier in this page's life). Without one it cannot succeed:
  //    /api/me is bearer-only (auth-package Protected), so on a full page
  //    load — module memory starts empty — it was a guaranteed 401, which
  //    every signed-in reload logged as a console error and the server
  //    aggregated as a warning (issue 738). The HttpOnly refresh cookie is
  //    invisible to JS, so "might have a cookie" is decided upstream by the
  //    signed-out gate (shouldBootstrapSession, routeGuard.ts) and the
  //    refresh itself is the probe: a definitive 401/404 there is the same
  //    "no session" verdict the old /api/me 401 + refresh 401 pair reached.
  if (accessToken) {
    try {
      cachedMe = await api<Me>('/api/me');
      return;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        cachedMe = null;
        accessToken = null;
        return;
      }
      // 401: the in-memory token is no longer accepted — fall through to
      // the silent refresh path.
    }
  }

  // 2. Try POST /api/auth/refresh — the HttpOnly refresh_token cookie
  //    survives reload, so the server can hand us a fresh access token
  //    without bouncing the user back to /login. CSRF needs no prior GET:
  //    with a cold token cache the api wrapper sends the token
  //    cmd/server/spa.go stamped into the shell's fexo-csrf-seed meta from
  //    the cookie it seeded on this very document response (issue 444), and
  //    a CSRF_INVALID deny still re-seeds and replays once (issue 408).
  try {
    const resp = await api<VerifyOrRefreshResponse>('/api/auth/refresh', {
      method: 'POST',
      body: { deviceId: getDeviceId() },
      // The server compares this against the session's bound class; a change
      // forces re-auth (DEVICE_CLASS_CHANGED). authDeviceClass mirrors the
      // server's derivation over the same resize-invariant hint sign-in sent
      // (authViewportHint), so an unchanged device matches the bound class at
      // any window width and only a genuine class switch trips the check.
      headers: { 'X-Device-Class': authDeviceClass() },
    });
    // Routed through setAccessToken (not a bare assignment) so a successful
    // silent refresh clears any stale signed-out flag too — this matters for
    // a browser that explicitly logged out and is now signing back in via
    // OAuth (the marker override in shouldBootstrapSession is what gets this
    // far at all; see routeGuard.ts).
    if (resp?.accessToken) setAccessToken(resp.accessToken);
  } catch (err) {
    // No fresh access token — the user is logged out. A 401 DEVICE_CLASS_CHANGED
    // is the one case that isn't an ordinary expiry: the device class changed
    // since sign-in, so the server forced re-auth. Flag it so boot can show the
    // "signed out for security" state instead of a silent bounce (FE-013); every
    // other rejection stays a plain logout.
    if (errorCodeOf(err) === 'DEVICE_CLASS_CHANGED') forcedReauth = true;
    // Only a DEFINITIVE 401 or 404 proves this browser has no session — that
    // is the one outcome where the server itself is telling us the refresh
    // cookie is expired/invalid/absent (or the session it named no longer
    // exists), so marking the signed-out flag here is safe: the next
    // anonymous view boots quietly instead of paying one more round-trip
    // (AC.4). ANY other failure — a 502/503 during a revision swap, a Cloud
    // Armor 429/403, a network drop — is transient and must NOT durably mark
    // this browser as signed out (Valentin's PR 471 review, finding 1): a
    // 30-day HttpOnly refresh cookie can easily outlive a 60s LB blip, and
    // marking it here would strand that cookie forever (no later boot would
    // ever retry the refresh again).
    if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
      markExplicitlySignedOut();
    }
    accessToken = null;
    cachedMe = null;
    return;
  }

  // 3. Re-probe /api/me with the fresh access token.
  try {
    cachedMe = await api<Me>('/api/me');
  } catch {
    accessToken = null;
    cachedMe = null;
  }
}

// reconcileActiveCompany makes the in-memory token carry the active company's
// role after boot (AUTH-001, rbac-spec §5.3): a bound session re-binds the same
// company to re-mint per-company (post-refresh, no user interaction), and a
// lone-company session auto-binds with no picker. A still-unbound multi-company
// session is left as-is for the boot flow's picker. Best-effort — a failure
// leaves the session unbound and the app fails closed on tenancy-gated calls,
// rather than wedging boot.
async function reconcileActiveCompany(): Promise<void> {
  const me = cachedMe;
  if (!me) return;
  try {
    if (me.companyId) {
      await switchCompany(me.companyId);
      return;
    }
    const companies = await listCompanies();
    if (companies.length === 1) {
      await switchCompany(companies[0].companyId);
    }
  } catch {
    // Leave unbound; tenancy-gated calls fail closed until a successful bind.
  }
}

// listCompanies returns the caller's active company memberships — the company
// picker's data (AUTH-001). Works while the session is unbound (pre-tenancy).
export async function listCompanies(): Promise<CompanyMembership[]> {
  const resp = await api<{ companies: CompanyMembership[] }>('/api/auth/companies');
  return resp?.companies ?? [];
}

// listAdministeredCompanies returns the subset of the caller's companies where the
// server resolved `administered` (RBAC-009): the companies the caller may grant
// memberships/invites in. This is server-authoritative — the client never infers
// it from roleId (mirror is stricter-never-looser, ADR-0010) — and is the source
// for the cross-company grant selector + the HQ fan-out target set.
export async function listAdministeredCompanies(): Promise<CompanyMembership[]> {
  const companies = await listCompanies();
  return companies.filter((c) => c.administered === true);
}

// switchCompany binds the session to companyId, swaps in the fresh access token
// the server mints with that company's role, and re-reads /api/me so the RBAC
// mirror + active-company display reflect the new role. Throws (ApiError) on a
// rejected companyId — the caller keeps its current binding.
//
// Refreshing the RBAC context here is what makes a switch reactive (RBAC-006.7):
// grants are per-company, so the mirror must be re-fetched, and doing it at this
// single choke point means no caller can forget. The context load broadcasts
// RBAC_CONTEXT_EVENT, which re-renders the nav — replacing the full page reload
// the switch used to need.
export async function switchCompany(companyId: string): Promise<void> {
  const resp = await api<CompanySwitchResult>('/api/auth/company', {
    method: 'POST',
    body: { companyId },
  });
  if (resp?.accessToken) accessToken = resp.accessToken;
  cachedMe = await api<Me>('/api/me');
  await loadRbacContext();
}

// refreshSelfSession re-mints the access token for a company whose role claim the
// caller just staled — by mutating their OWN membership (save flow, issue 191) or
// after a stale-role deny (the api() interceptor). The tenancy gate fails closed on
// a claim/membership mismatch (D3 coherence, rbac-spec §5.3) — surfacing as the
// misleading "no active company" — and a self-role-change leaves the in-memory
// token carrying the stale role. This mirrors reconcileActiveCompany's reload path
// WITHOUT a reload: switchCompany re-mints via POST /api/auth/company, re-reads
// /api/me and reloads the RBAC context (broadcasting RBAC_CONTEXT_EVENT). Refreshing
// /api/me + loadRbacContext alone is insufficient — neither re-mints the token.
//
// companyId is the company to re-mint. Callers pass it explicitly (issue 191): the
// server's write result carries the mutated company's id, so the re-mint works even
// when the in-memory session is still unbound (cachedMe.companyId unset) — the gap
// the issue 176 workaround left open. Absent an explicit id it falls back to the bound
// active company; with neither there is nothing to re-mint, so it degrades to a
// best-effort context refresh.
export async function refreshSelfSession(companyId?: string): Promise<void> {
  const target = companyId ?? cachedMe?.companyId;
  if (target) {
    await switchCompany(target);
    // A completed re-mint means the session is coherent again, so the loop bound has
    // done its job and must not suppress the recovery for some LATER role change.
    clearRoleChangeReauthSentinel();
    return;
  }
  await loadRbacContext();
}

// renewAccessToken is the mid-session counterpart of establishSession's silent
// refresh (issue 712): the short-lived access token has expired under an open page —
// a long authoring session on an admin editor — and a request just answered 401.
// POST /api/auth/refresh mints a new token off the HttpOnly refresh cookie, and the
// bound company is re-bound so the fresh token carries the ACTIVE company's role
// (the refresh mints the global role, exactly as at boot — reconcileActiveCompany).
// Throws when the refresh itself is refused: that is the definitive "no session"
// signal (the cookie is expired, revoked or absent), and the caller ends the session
// by name. A failed re-bind is left best-effort like boot's: the replay then meets
// the tenancy gate, which fails closed.
export async function renewAccessToken(): Promise<void> {
  const resp = await api<VerifyOrRefreshResponse>('/api/auth/refresh', {
    method: 'POST',
    body: { deviceId: getDeviceId() },
    headers: { 'X-Device-Class': authDeviceClass() },
  });
  if (!resp?.accessToken) throw new Error('refresh returned no access token');
  setAccessToken(resp.accessToken);
  const bound = cachedMe?.companyId;
  if (bound) {
    try {
      await switchCompany(bound);
    } catch {
      // Leave the global-role token in place; tenancy-gated calls fail closed.
    }
  }
}

export function isAuthenticated(): boolean {
  return cachedMe !== null;
}

export function currentUser(): Me | null {
  return cachedMe;
}

// The captcha module is loaded on demand (issue 774): it is needed only on the
// login screen, yet a static import put its body AND the inlined site key into
// the `api` chunk that every first load fetches — ~350 B gz the CI gate could
// not see, because the local build has no key and tree-shakes the lot. The
// chunk is not precached (build-sw.mjs follows static imports only), so an
// offline or blocked fetch of it is one more best-effort failure: null token,
// no header, login proceeds.
async function captchaTokenLazy(action: string): Promise<string | null> {
  try {
    const { captchaToken } = await import('./captcha');
    return await captchaToken(action);
  } catch {
    return null;
  }
}

export async function requestMagicLink(email: string): Promise<void> {
  // Best-effort: a null token means the captcha could not be reached (no site
  // key, blocked script, slow network). We send no header and the server's
  // fail-open policy lets the request through — a captcha problem must never
  // be the reason someone cannot sign in.
  const token = await captchaTokenLazy(MAGIC_LINK_ACTION);
  await api('/api/auth/magic-link', {
    method: 'POST',
    body: { email },
    headers: token ? { 'X-Recaptcha-Token': token } : undefined,
  });
}

// requestAccountRecovery starts the lost-passkey flow. Like the magic
// link, the server returns the same generic response for any email (no
// enumeration); a 429 surfaces as ApiError for the caller to handle.
export async function requestAccountRecovery(email: string): Promise<void> {
  await api('/api/auth/account-recovery', { method: 'POST', body: { email } });
}

export type OAuthProvider = 'google' | 'apple';

// Begin an OAuth sign-in. Unlike the magic-link POST this is a full-page
// navigation, not a fetch: the init endpoint 302s to the provider's consent
// screen, the provider redirects to /api/auth/oauth/{provider}/callback
// (handled server-side, which sets the refresh cookie), and the browser lands
// back on /?auth=success — where authInit() exchanges the cookie for a token on
// the next boot. deviceId rides along so the session is pinned to this browser
// for the MaxSessions cap (same id used by verify + refresh).
export function startOAuth(provider: OAuthProvider): void {
  const deviceId = encodeURIComponent(getDeviceId());
  // The server derives the authoritative device_class from User-Agent +
  // viewportWidth (authViewportHint: resize-invariant, see viewport.ts); both
  // ride through the encrypted OAuth state to the callback. deviceClass is a
  // transitional fallback the server ignores.
  const viewportWidth = encodeURIComponent(String(authViewportHint()));
  const deviceClass = encodeURIComponent(authDeviceClass());
  window.location.assign(
    `/api/auth/oauth/${provider}?deviceId=${deviceId}&viewportWidth=${viewportWidth}&deviceClass=${deviceClass}`,
  );
}

// Maps the auth error codes the OAuth callback can hand back via
// ?auth=error&code=… (app.ts normalizes that landing to /login?error=…) to
// user-facing copy. Unknown codes fall back to a generic message that nudges
// toward the always-available magic-link path.
export function getAuthErrorMessage(code: string): string {
  switch (code) {
    case 'OAUTH_DENIED':
      return t('authError.denied');
    case 'OAUTH_EMAIL_MISSING':
      return t('authError.emailMissing');
    case 'OAUTH_EMAIL_UNVERIFIED':
      return t('authError.emailUnverified');
    case 'DEVICE_LIMIT_EXCEEDED':
      return t('authError.deviceLimit');
    case 'OAUTH_STATE_INVALID':
      return t('authError.stateInvalid');
    default:
      return t('authError.generic');
  }
}

export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } finally {
    accessToken = null;
    // A user-initiated sign-out is a DEFINITIVE "no session" signal (issue
    // 444 redesign, finding 2): mark it so the next anonymous boot on this
    // browser skips the probes instead of attempting and 401ing for nothing.
    markExplicitlySignedOut();
    cachedMe = null;
    // Drop the mirror too, so the next session never renders a moment of the
    // previous user's grants.
    clearRbacContext();
    // In-memory state is not the only thing the session owns: the service worker
    // caches authenticated /api/* GET responses, keyed by URL with no session
    // identity, so without this they outlive the sign-out until the next deploy
    // bumps the cache version — and on a shared device an offline boot would serve
    // the previous caller's data (issue 205). AWAITED, not fire-and-forget: every
    // sign-out path navigates immediately after this returns, and a purge racing
    // that navigation is the one thing that would defeat it. This is the single
    // seam — forceReauth and all three UI sign-out call sites go through logout().
    await purgeSessionCaches();
  }
}

// In-flight guard so a burst of stale-role denies (several admin requests racing)
// issues exactly one logout + redirect, never a double-logout or a redirect storm.
// It covers the concurrent window before the navigation only — it dies with the
// document, which is what REAUTH_SENTINEL_KEY below exists to cover.
let reauthInFlight = false;

// The cross-navigation loop bound (issue 204). forceReauth hard-navigates, so the
// in-memory latch above cannot see the next document: a fresh sign-in that visits
// Admin again while the server-side stale window is still open re-trips the gate with
// a brand-new latch and gets bounced again. (The premise this replaces — "a logged-out
// session can't re-trip the stale gate" — holds for the OLD session but not for a NEW
// one minted inside the window.) So record the logout in sessionStorage and refuse to
// fire a second one inside a short TTL: the request then simply surfaces its deny, the
// page shows the recovery status, and the session survives instead of looping.
//
// The TTL is deliberately longer than the server's 60 s membership-cache window, so
// the bound outlives the condition that would re-trip it.
const REAUTH_SENTINEL_KEY = 'app.reauth.roleChanged';
const REAUTH_SENTINEL_TTL_MS = 90_000;

// reauthRecently reports whether a role-changed reauth already fired inside the TTL.
// Storage is best-effort: private mode or disabled storage throws, and the honest
// fallback is to allow the reauth — losing the loop bound is better than trapping a
// genuinely incoherent session on a page it cannot use.
function reauthRecently(): boolean {
  try {
    const raw = sessionStorage.getItem(REAUTH_SENTINEL_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < REAUTH_SENTINEL_TTL_MS;
  } catch {
    return false;
  }
}

function markReauth(): void {
  try {
    sessionStorage.setItem(REAUTH_SENTINEL_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — see reauthRecently: the loop bound degrades, the
    // recovery itself still works.
  }
}

// clearRoleChangeReauthSentinel drops the loop bound once the session is demonstrably
// coherent again, so a later, unrelated role change is not silently denied its one
// logout. Called after a successful re-mint.
export function clearRoleChangeReauthSentinel(): void {
  try {
    sessionStorage.removeItem(REAUTH_SENTINEL_KEY);
  } catch {
    // Nothing to do — an unreadable store cannot hold a stale sentinel either.
  }
}

// forceReauth ends the current session and sends the user to /login to sign in
// again, carrying a reason the login screen can surface (issue 200). It is the
// recovery for a self-role-change the in-place re-mint can't resolve: rather than
// reloading the tab against a stale claim, it logs out (POST /api/auth/logout, then
// clears the in-memory token + RBAC mirror via logout()) and hard-navigates to
// /login — a brand-new session on the next sign-in carries the fresh role with zero
// stale-claim ambiguity, and the server-side revoke makes it strictly safer than a
// reload. Distinct from the DEVICE_CLASS_CHANGED forcedReauth flag above, which is a
// boot-time refresh rejection, not an explicit logout. The hard navigation mirrors
// compromise.ts's redirectToLogin so no in-memory state survives.
export async function forceReauth(reason: string): Promise<void> {
  if (reauthInFlight) return;
  // The role-changed reason is the loop-prone one: it is triggered by the same
  // server-side window that the next session can walk straight back into. Other
  // reasons are one-off conditions and are not bounded here.
  if (reason === 'role-changed') {
    if (reauthRecently()) return;
    markReauth();
  }
  reauthInFlight = true;
  await logout();
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign('/login?reason=' + encodeURIComponent(reason));
  }
}
